/** Shared due-date range helpers for CRM task lists. */

export const toYmd = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

export const TASK_DATE_PRESETS = [
    { label: "1m", months: 1 },
    { label: "3m", months: 3 },
    { label: "6m", months: 6 },
];

/** N months back through N months ahead (includes future follow-ups). */
export const getTaskDueDateRangeForMonths = (months = 1) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = new Date(today);
    from.setMonth(from.getMonth() - months);
    const to = new Date(today);
    to.setMonth(to.getMonth() + months);
    return { from: toYmd(from), to: toYmd(to), months };
};

/** @deprecated Use empty from/to for no filter. Kept for any legacy callers. */
export const getDefaultTaskDueDateRange = () => getTaskDueDateRangeForMonths(1);

export const isTaskDateFilterActive = (from, to) => Boolean(from && to);

/** True when no filter, complete range, or invalid complete range (caller checks separately). */
export const isTaskDateFilterComplete = (from, to) => {
    if (!from && !to) return true;
    return Boolean(from && to);
};

export const isValidTaskDateRange = (from, to) => {
    if (!from && !to) return true;
    if (!from || !to) return false;
    return from <= to;
};

/** Returns API query params, {} when no filter, null when range is invalid. */
export const buildTaskDateQueryParams = (from, to) => {
    if (!isTaskDateFilterActive(from, to)) return {};
    if (!isValidTaskDateRange(from, to)) return null;
    return { due_date_from: from, due_date_to: to };
};

export const formatYmdForDisplay = (ymd) => {
    if (!ymd) return "";
    return new Date(`${ymd}T00:00:00`).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
};

/** Human-readable note for the active (or absent) due-date filter. */
export const getTaskDateRangeStatusMessage = (from, to) => {
    if (!from && !to) {
        return "Showing all tasks — no due date filter applied. Includes overdue, due today, and future follow-ups.";
    }
    if (from && !to) {
        return "Select a To date to apply the filter, or use Clear to show all tasks again.";
    }
    if (!from && to) {
        return "Select a From date to apply the filter, or use Clear to show all tasks again.";
    }
    if (!isValidTaskDateRange(from, to)) {
        return "To date must be on or after From date.";
    }
    const activePreset = matchTaskDatePresetMonths(from, to);
    const presetPart = activePreset ? ` Quick filter: last ${activePreset} month${activePreset > 1 ? "s" : ""} and next ${activePreset} month${activePreset > 1 ? "s" : ""}.` : "";
    return `Showing tasks due from ${formatYmdForDisplay(from)} to ${formatYmdForDisplay(to)}.${presetPart} Past, today, and future tasks within this range are included.`;
};

export const getTaskDateFilterEmptyMessage = (from, to) =>
    isTaskDateFilterActive(from, to)
        ? "No tasks found for this date range."
        : "No tasks yet.";

/** Expand an existing range so a task due date is included. */
export const expandTaskDateRangeToInclude = (from, to, ymd) => {
    if (!ymd) return { from, to };
    let nextFrom = from;
    let nextTo = to;
    if (!nextFrom || ymd < nextFrom) nextFrom = ymd;
    if (!nextTo || ymd > nextTo) nextTo = ymd;
    return { from: nextFrom, to: nextTo };
};

/** Returns matching preset months if from/to equal that preset, else null. */
export const matchTaskDatePresetMonths = (from, to) => {
    if (!from || !to) return null;
    for (const preset of TASK_DATE_PRESETS) {
        const range = getTaskDueDateRangeForMonths(preset.months);
        if (range.from === from && range.to === to) return preset.months;
    }
    return null;
};
